import { createFileRoute, Link } from "@tanstack/react-router";
import { useEffect, useRef } from "react";
import { Layout } from "@/components/Layout";
import { hreflangLinks } from "@/lib/seo";
import {
  ArrowRight,
  Compass,
  HandHeart,
  Heart,
  Leaf,
  LockKeyhole,
  MessagesSquare,
  RotateCcw,
  ShieldCheck,
  Sprout,
  Truck,
} from "lucide-react";
import hero from "@/assets/meista-hero.jpg";
// @ts-expect-error vite-imagetools transforms query at build time
import heroAvif1600 from "@/assets/meista-hero.jpg?format=avif&w=1600&quality=66";
// @ts-expect-error vite-imagetools transforms query at build time
import heroAvif900 from "@/assets/meista-hero.jpg?format=avif&w=900&quality=66";
// @ts-expect-error vite-imagetools transforms query at build time
import heroWebp1600 from "@/assets/meista-hero.jpg?format=webp&w=1600&quality=72";
// @ts-expect-error vite-imagetools transforms query at build time
import heroWebp900 from "@/assets/meista-hero.jpg?format=webp&w=900&quality=72";
import about from "@/assets/about.jpg";
// @ts-expect-error vite-imagetools transforms query at build time
import aboutAvif from "@/assets/about.jpg?format=avif&w=1200&quality=70";
// @ts-expect-error vite-imagetools transforms query at build time
import aboutWebp from "@/assets/about.jpg?format=webp&w=1200&quality=75";
import hands from "@/assets/montessori-hands.jpg";
// @ts-expect-error vite-imagetools transforms query at build time
import handsAvif from "@/assets/montessori-hands.jpg?format=avif&w=1000&quality=66";
// @ts-expect-error vite-imagetools transforms query at build time
import handsWebp from "@/assets/montessori-hands.jpg?format=webp&w=1000&quality=72";
import climbing from "@/assets/product-climbing.jpg";
import balance from "@/assets/product-balance.jpg";
import modular from "@/assets/product-modular.jpg";

const CANONICAL = "https://montessori24.fi/meista";

export const Route = createFileRoute("/meista")({
  head: () => ({
    meta: [
      { title: "Meistä — suomalainen Montessori-perheyritys | Montessori24" },
      { name: "description", content: "Suomalainen perheyritys ja Montessori-kalusteiden verkkokauppa. Valitsemme jokaisen tuotteen tukemaan lapsen itsenäisyyttä, keskittymistä ja luovuutta." },
      { property: "og:title", content: "Meistä — suomalainen Montessori-perheyritys" },
      { property: "og:description", content: "Miksi Montessori24 syntyi? Tarina perheyrityksestä, joka haluaa antaa lapsille leluja ja kalusteita, jotka eivät huuda huomiota — vaan kasvattavat itsenäisyyttä." },
      { property: "og:url", content: CANONICAL },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary" },
      {
        "script:ld+json": {
          "@context": "https://schema.org",
          "@graph": [
            {
              "@type": "AboutPage",
              name: "Meistä — Montessori24",
              url: CANONICAL,
              description:
                "Montessori24 on suomalainen perheyritys (MLR Solutions Oy, Y-tunnus 3582909-7), joka tuo suomalaisiin koteihin huolella valittuja Montessori-kalusteita ja -leluja.",
              inLanguage: "fi-FI",
              isPartOf: { "@type": "WebSite", name: "Montessori24", url: "https://montessori24.fi" },
            },
            {
              "@type": "BreadcrumbList",
              itemListElement: [
                { "@type": "ListItem", position: 1, name: "Etusivu", item: "https://montessori24.fi" },
                { "@type": "ListItem", position: 2, name: "Meistä", item: CANONICAL },
              ],
            },
          ],
        },
      },
    ],
    links: [{ rel: "canonical", href: CANONICAL }, ...hreflangLinks(CANONICAL)],
  }),
  component: About,
});

/** Soft scroll-reveal wrapper — adds .reveal-in when the block enters view. */
function Reveal({
  children,
  className = "",
  delay = 0,
}: {
  children: React.ReactNode;
  className?: string;
  delay?: number;
}) {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const io = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          el.classList.add("reveal-in");
          io.disconnect();
        }
      },
      { threshold: 0.12 },
    );
    io.observe(el);
    return () => io.disconnect();
  }, []);
  return (
    <div ref={ref} className={`reveal ${className}`} style={{ transitionDelay: `${delay}ms` }}>
      {children}
    </div>
  );
}

function Eyebrow({ children }: { children: React.ReactNode }) {
  return (
    <span className="text-xs font-medium uppercase tracking-[0.25em] text-clay">{children}</span>
  );
}

const WHY_CARDS = [
  { icon: HandHeart, title: "Huolella valitut tuotteet", text: "Valitsemme tuotteita valmistajan tuotetietojen, materiaalien, käyttötarkoituksen ja saatavilla olevien turvallisuustietojen perusteella." },
  { icon: ShieldCheck, title: "Turvallisuus ennen kaikkea", text: "Valitsemme tuotteita tunnetuilta valmistajilta ja ilmoitamme tuotekohtaiset turvallisuus-, materiaali- ja vaatimustenmukaisuustiedot mahdollisimman selkeästi jokaisella tuotesivulla." },
  { icon: Leaf, title: "Skandinaavinen design", text: "Selkeät linjat, luonnolliset materiaalit ja värit, jotka sopivat suomalaiseen kotiin eivätkä ärsytä silmää." },
  { icon: Truck, title: "Selkeä toimitus", text: "Varastossa olevat tuotteet pyritään lähettämään 1–2 arkipäivän kuluessa. Toimitus Suomeen kestää tyypillisesti 3–7 arkipäivää, ja lähetyksen alkuperä kerrotaan tuotesivulla." },
  { icon: MessagesSquare, title: "Asiakaspalvelu suomeksi", text: "Vastaamme itse, yleensä samana arkipäivänä. Saat aina ihmisen, et bottia — ja vastauksen äidinkielelläsi." },
  { icon: RotateCcw, title: "30 päivän palautusetu", text: "Kokeile tuotetta rauhassa kotona. Jos se ei sovi perheeseenne, palautus on maksuton ja vaivaton." },
  { icon: LockKeyhole, title: "Turvalliset maksutavat", text: "Verkkopankit, kortit, MobilePay ja Klarna. Maksuliikenne kulkee salattuna Shopifyn turvallisen kassan kautta." },
];

const VALUES = [
  ["Minimalismi", "Vähemmän, mutta paremmin. Yksi hyvä lelu korvaa kymmenen huonoa."],
  ["Laatu", "Koivuvaneri ja viimeistely, joka kestää sisaruksesta toiseen."],
  ["Turvallisuus", "Tuotekohtaiset turvallisuus- ja materiaalitiedot avoimesti esillä jokaisella tuotesivulla."],
  ["Lapsen kehitys", "Jokainen tuote tukee jotakin oikeaa taitoa — motoriikkaa, keskittymistä tai itsenäisyyttä."],
  ["Kestävyys", "Tuotteet, joita ei heitetä pois. Vaihto-osia saatavilla, ja käytettynäkin arvonsa säilyttävää."],
];

function About() {
  return (
    <Layout>
      {/* 1. Hero */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0">
          <picture>
            <source
              type="image/avif"
              srcSet={`${heroAvif900} 900w, ${heroAvif1600} 1600w`}
              sizes="100vw"
            />
            <source
              type="image/webp"
              srcSet={`${heroWebp900} 900w, ${heroWebp1600} 1600w`}
              sizes="100vw"
            />
            <img
              src={hero}
              alt="Äiti ja taapero leikkivät yhdessä puisilla Montessori-leluilla auringonpaisteisessa olohuoneessa"
              width={1920}
              height={1088}
              fetchPriority="high"
              decoding="async"
              className="h-full w-full object-cover object-center"
            />
          </picture>
          {/* Cream wash keeps text WCAG AA on every part of the photo. */}
          <div className="absolute inset-0 bg-gradient-to-r from-background/95 via-background/70 to-background/15" />
          <div className="absolute inset-x-0 bottom-0 h-24 bg-gradient-to-t from-background/80 to-transparent" />
        </div>
        <div className="container-prose relative flex min-h-[68vh] items-center py-20 md:min-h-[78vh]">
          <div className="max-w-xl">
            <span className="hero-fade-up inline-block text-xs font-medium uppercase tracking-[0.25em] text-clay">
              Meistä · Suomalainen perheyritys
            </span>
            <h1 className="hero-fade-up hero-fade-up-2 mt-4 text-5xl text-foreground md:text-7xl text-balance">
              Kasvamme yhdessä lapsesi kanssa.
            </h1>
            <p className="hero-fade-up hero-fade-up-3 mt-6 text-lg leading-relaxed text-foreground/85 font-light">
              Montessori24 syntyi yhdestä ajatuksesta: suomalaiset lapset ansaitsevat
              leluja ja kalusteita, jotka eivät huuda huomiota itselleen — vaan antavat
              lapsen löytää keskittymisen, itsenäisyyden ja tekemisen ilon.
            </p>
            <div className="hero-fade-up hero-fade-up-4 mt-8">
              <Link
                to="/kauppa"
                className="inline-flex items-center gap-2 rounded-full bg-primary px-7 py-3.5 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90"
              >
                Tutustu valikoimaan
                <ArrowRight className="size-4" aria-hidden="true" />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* 2. Our Story */}
      <section className="container-prose py-20 md:py-28">
        <div className="grid items-center gap-12 md:grid-cols-2 md:gap-16">
          <Reveal>
            <picture>
              <source type="image/avif" srcSet={aboutAvif} />
              <source type="image/webp" srcSet={aboutWebp} />
              <img
                src={about}
                alt="Suomalainen lapsi leikkii Montessori-oppimistornissa kotona"
                width={800}
                height={960}
                loading="lazy"
                decoding="async"
                className="aspect-[5/6] w-full rounded-3xl object-cover"
              />
            </picture>
          </Reveal>
          <Reveal delay={120}>
            <Eyebrow>Tarinamme</Eyebrow>
            <h2 className="mt-3 text-3xl text-foreground md:text-4xl text-balance">
              Kaikki alkoi olohuoneen lattialta
            </h2>
            <p className="mt-6 leading-relaxed text-muted-foreground">
              Esikoisemme yritti kiivetä sohvan selkänojalle kuudetta kertaa peräkkäin.
              Ympärillä oli leluja, jotka vilkkuvat, pärisevät ja kilisevät — ne vaativat
              lapsen huomion itselleen, mutta eivät antaneet mitään takaisin. Tajusimme,
              että lapsi ei kaipaa enempää ärsykkeitä. Hän kaipaa mahdollisuuksia.
            </p>
            <p className="mt-5 leading-relaxed text-muted-foreground">
              Kun tutustuimme Montessori-ajatteluun, kaikki loksahti paikoilleen: rauhallinen
              ympäristö, aidot materiaalit ja kalusteet, jotka on mitoitettu lapselle —
              eivät aikuiselle. Mutta kun etsimme niitä suomalaisista verkkokaupoista,
              löysimme joko heikkolaatuista tavaraa tai viikkojen toimitusajat.
            </p>
            <p className="mt-5 leading-relaxed text-muted-foreground">
              Niinpä perustimme Montessori24:n. Olemme{" "}
              <span className="text-foreground">MLR Solutions Oy</span>, pieni suomalainen
              perheyritys (Y-tunnus 3582909-7), ja jokainen valikoimamme tuote on sellainen,
              jonka itse toisimme kotiimme.
            </p>
            <blockquote className="mt-8 border-l-2 border-clay pl-5 text-lg font-light italic leading-relaxed text-foreground">
              &ldquo;Emme myy leluja. Myymme välineitä, joilla lapsi saa kokeilla,
              kaatua ja onnistua itse.&rdquo;
            </blockquote>
            <p className="mt-3 text-xs uppercase tracking-[0.2em] text-muted-foreground">
              Montessori24:n perustajat
            </p>
          </Reveal>
        </div>
      </section>

      {/* 3. Our Mission */}
      <section className="bg-sand/70">
        <div className="container-prose py-20 md:py-28">
          <Reveal className="mx-auto max-w-2xl text-center">
            <Eyebrow>Missiomme</Eyebrow>
            <h2 className="mt-3 text-3xl text-foreground md:text-4xl text-balance">
              Jokaisella tuotteella on tarkoitus
            </h2>
            <p className="mt-5 leading-relaxed text-muted-foreground">
              Emme täytä hyllyjä. Valitsemme valikoimaan vain tuotteita, jotka auttavat
              lasta kolmessa tärkeimmässä asiassa.
            </p>
          </Reveal>
          <div className="mt-12 grid gap-6 md:grid-cols-3">
            {[
              {
                icon: Compass,
                title: "Itsenäisyys",
                text: <>Kalusteet ja lelut, joita lapsi voi käyttää ilman aikuisen apua — kiivetä, kantaa, avata ja rakentaa omin käsin.</>,
              },
              {
                icon: Heart,
                title: "Itseluottamus",
                text: <>Kun lapsi onnistuu itse, usko omiin kykyihin vahvistuu. &ldquo;Minä osaan!&rdquo; on paras lause, jonka kotona voi kuulla.</>,
              },
              {
                icon: Sprout,
                title: "Luonnollinen kehitys",
                text: <>Oikea tuote oikeaan ikävaiheeseen tukee motoriikkaa, keskittymistä ja luovuutta — leikin kautta, ei suorittamalla.</>,
              },
            ].map(({ icon: Icon, title, text }, i) => (
              <Reveal key={title} delay={i * 100}>
                <div className="h-full rounded-3xl border border-border bg-card p-8">
                  <span className="inline-flex size-12 items-center justify-center rounded-2xl bg-sand text-sage">
                    <Icon className="size-6" aria-hidden="true" />
                  </span>
                  <h3 className="mt-5 text-xl text-foreground">{title}</h3>
                  <p className="mt-3 text-sm leading-relaxed text-muted-foreground">{text}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* 4. Why choose Montessori24 */}
      <section className="container-prose py-20 md:py-28">
        <Reveal className="mx-auto max-w-2xl text-center">
          <Eyebrow>Miksi Montessori24</Eyebrow>
          <h2 className="mt-3 text-3xl text-foreground md:text-4xl text-balance">
            Kauppa, josta itsekin haluaisimme ostaa
          </h2>
        </Reveal>
        <div className="mt-12 grid gap-5 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
          {WHY_CARDS.map(({ icon: Icon, title, text }, i) => (
            <Reveal key={title} delay={(i % 4) * 80}>
              <div className="group h-full rounded-3xl border border-border bg-card p-7 transition-all duration-300 hover:-translate-y-1 hover:shadow-[0_18px_40px_-24px_rgba(47,47,47,0.35)]">
                <span className="inline-flex size-11 items-center justify-center rounded-xl bg-sand text-sage transition-colors group-hover:bg-primary group-hover:text-primary-foreground">
                  <Icon className="size-5" aria-hidden="true" />
                </span>
                <h3 className="mt-4 text-lg text-foreground">{title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{text}</p>
              </div>
            </Reveal>
          ))}
          {/* 7 cards + soft brand tile keeps the grid balanced on xl. */}
          <Reveal delay={240}>
            <Link
              to="/montessori-koulu"
              className="flex h-full flex-col justify-between rounded-3xl bg-primary p-7 text-primary-foreground transition-colors hover:bg-primary/90"
            >
              <div>
                <h3 className="text-lg">Ilmainen Montessori-koulu</h3>
                <p className="mt-2 text-sm leading-relaxed text-primary-foreground/95">
                  Oppaita, ikävinkkejä ja turvallisuusartikkeleita vanhemmille —
                  täysin ilmaiseksi.
                </p>
              </div>
              <span className="mt-6 inline-flex items-center gap-2 text-sm font-medium">
                Lue oppaita
                <ArrowRight className="size-4" aria-hidden="true" />
              </span>
            </Link>
          </Reveal>
        </div>
      </section>

      {/* 5. What Montessori means */}
      <section className="bg-sand/70">
        <div className="container-prose grid items-center gap-12 py-20 md:grid-cols-2 md:gap-16 md:py-28">
          <Reveal className="order-2 md:order-1">
            <Eyebrow>Mitä Montessori tarkoittaa</Eyebrow>
            <h2 className="mt-3 text-3xl text-foreground md:text-4xl text-balance">
              Ei menetelmä — vaan tapa katsoa lasta
            </h2>
            <p className="mt-6 leading-relaxed text-muted-foreground">
              Maria Montessori oli italialainen lääkäri, joka huomasi yli sata vuotta sitten
              jotakin yksinkertaista: lapsi oppii parhaiten tekemällä itse, rauhallisessa
              ympäristössä, omassa tahdissaan. Ei kiirehtimällä, ei kilpailemalla — vaan
              keskittymällä.
            </p>
            <p className="mt-5 leading-relaxed text-muted-foreground">
              Käytännössä se näkyy kotona pieninä asioina: takki roikkuu lapsen korkeudella,
              lelut ovat näkyvillä eivätkä piilossa laatikossa, ja keittiössä voi seistä
              turvallisesti mukana tekemässä. Lapsi ei ole vieras aikuisten maailmassa,
              vaan osallistuja siinä.
            </p>
            <p className="mt-5 leading-relaxed text-muted-foreground">
              Siksi valikoimamme tuotteet ovat hiljaisia, puisia ja selkeitä. Ne eivät
              kerro lapselle, mitä hänen pitäisi tehdä — ne kysyvät, mitä hän haluaa
              kokeilla seuraavaksi.
            </p>
            <Link
              to="/montessori-koulu"
              className="mt-7 inline-flex items-center gap-2 text-sm font-medium text-foreground underline-offset-4 hover:underline"
            >
              Syvenny aiheeseen ilmaisessa Montessori-koulussamme
              <ArrowRight className="size-4" aria-hidden="true" />
            </Link>
          </Reveal>
          <Reveal delay={120} className="order-1 md:order-2">
            <picture>
              <source type="image/avif" srcSet={handsAvif} />
              <source type="image/webp" srcSet={handsWebp} />
              <img
                src={hands}
                alt="Pienen lapsen käsi asettelee huolellisesti puupalikoita päällekkäin vaalealla pöydällä"
                width={1000}
                height={1000}
                loading="lazy"
                decoding="async"
                className="aspect-square w-full rounded-3xl object-cover"
              />
            </picture>
          </Reveal>
        </div>
      </section>

      {/* 6. Trust */}
      <section className="container-prose py-20 md:py-28">
        <Reveal className="mx-auto max-w-2xl text-center">
          <Eyebrow>Luottamus</Eyebrow>
          <h2 className="mt-3 text-3xl text-foreground md:text-4xl text-balance">
            Miksi Montessori24?
          </h2>
          <p className="mt-4 text-sm leading-relaxed text-muted-foreground">
            Emme esitä asiakasmääriä tai tähtiarvosanoja, joita emme voi todentaa. Kerromme sen
            sijaan avoimesti, kuka tuotteen valmistaa ja mitä siitä tiedetään.
          </p>
        </Reveal>

        <Reveal className="mt-12">
          <ul className="grid gap-4 rounded-3xl border border-border bg-card px-8 py-10 sm:grid-cols-2">
            {[
              "Huolella valitut eurooppalaiset valmistajat",
              "Selkeät turvallisuus- ja tuotetiedot jokaisella tuotesivulla",
              "Suomalainen asiakaspalvelu suomeksi",
              "Turvalliset maksutavat",
              "30 päivän palautusetu",
              "Suomalainen yritys: MLR Solutions Oy",
            ].map((item) => (
              <li key={item} className="flex items-start gap-3 text-sm text-foreground">
                <ShieldCheck className="mt-0.5 size-4 shrink-0 text-clay" strokeWidth={1.5} aria-hidden />
                <span>{item}</span>
              </li>
            ))}
          </ul>
        </Reveal>

        <Reveal className="mt-10">
          <p className="text-center text-xs uppercase tracking-[0.2em] text-muted-foreground">
            Tuotteita valikoimastamme
          </p>
          <div className="mt-5 grid grid-cols-3 gap-3 md:gap-5">
            {[
              [climbing, "Iglu-kiipeilysetti lapsenhuoneen nurkassa"],
              [balance, "Tasapainokivet olohuoneen lattialla leikin aikana"],
              [modular, "Modulaarinen leikkisetti kotona arjen keskellä"],
            ].map(([src, alt]) => (
              <img
                key={alt as string}
                src={src as string}
                alt={alt as string}
                width={600}
                height={600}
                loading="lazy"
                decoding="async"
                className="aspect-square w-full rounded-2xl object-cover"
              />
            ))}
          </div>
        </Reveal>
      </section>

      {/* 7. Brand values */}
      <section className="border-y border-border bg-background">
        <div className="container-prose py-20 md:py-24">
          <Reveal className="max-w-xl">
            <Eyebrow>Arvomme</Eyebrow>
            <h2 className="mt-3 text-3xl text-foreground md:text-4xl text-balance">
              Viisi asiaa, joista emme tingi
            </h2>
          </Reveal>
          <dl className="mt-10">
            {VALUES.map(([title, text], i) => (
              <Reveal key={title} delay={i * 60}>
                <div className="grid gap-2 border-t border-border py-6 last:border-b sm:grid-cols-12 sm:items-baseline">
                  <dt className="flex items-baseline gap-4 sm:col-span-4">
                    <span className="text-xs font-medium text-clay">
                      {String(i + 1).padStart(2, "0")}
                    </span>
                    <span className="text-xl text-foreground">{title}</span>
                  </dt>
                  <dd className="text-sm leading-relaxed text-muted-foreground sm:col-span-7 sm:col-start-6">
                    {text}
                  </dd>
                </div>
              </Reveal>
            ))}
          </dl>
        </div>
      </section>

      {/* 8. CTA */}
      <section className="container-prose py-20 md:py-28">
        <Reveal>
          <div className="relative overflow-hidden rounded-[2rem] bg-primary px-8 py-16 text-center md:py-24">
            <div className="pointer-events-none absolute -right-20 -top-20 size-64 rounded-full bg-primary-foreground/10" />
            <div className="pointer-events-none absolute -bottom-24 -left-16 size-72 rounded-full bg-primary-foreground/10" />
            <h2 className="relative mx-auto max-w-2xl text-3xl text-primary-foreground md:text-5xl text-balance">
              Anna lapsen kasvaa omalla tavallaan.
            </h2>
            <p className="relative mx-auto mt-5 max-w-xl leading-relaxed text-primary-foreground/95">
              Tutustu valikoimaan ja löydä tuotteet, jotka tukevat lapsesi itsenäisyyttä —
              tänään ja vuosien päästä.
            </p>
            <div className="relative mt-9 flex flex-col items-center justify-center gap-4 sm:flex-row">
              <Link
                to="/kauppa"
                className="inline-flex items-center gap-2 rounded-full bg-card px-8 py-4 text-sm font-medium text-foreground transition-colors hover:bg-card/90"
              >
                Tutustu valikoimaan
                <ArrowRight className="size-4" aria-hidden="true" />
              </Link>
              <a
                href="mailto:asiakaspalvelu@montessori24.fi"
                className="text-sm text-primary-foreground/95 underline-offset-4 hover:text-primary-foreground hover:underline"
              >
                Kysy meiltä — vastaamme suomeksi
              </a>
            </div>
          </div>
        </Reveal>
      </section>
    </Layout>
  );
}
